import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import format from 'string-template';
import moment from 'moment';

import colors from '../../../../styles/colors';
import { formatDuration } from '../../../../utils/time';
import {
  HeartRateZones,
  buildHeartRateChart,
  ACTIVE_TIME_FORMAT,
  formatNumber,
  getTimeInZones,
} from './workoutHealthData';
import {
  zoneColors,
  DetailsRow,
  DetailsCard,
  CardHeader,
  ChartSvg,
  ZoneRow,
  ZoneTrack,
  ZoneFill,
  ZoneTime,
  CaloriesRow,
  Strong,
  Muted,
} from './styles';
import texts from './texts.json';

const AXIS_FONT = { fontSize: 11, fill: colors.shades.gamma10 };

const HeartRateChart = ({ chart, averageHeartRate }) => (
  <ChartSvg
    viewBox={`0 0 ${chart.width} ${chart.height}`}
    role="img"
    aria-label={format(texts.heartRateAria, {
      minutes: chart.totalMinutes,
      average: averageHeartRate ?? '-',
      peak: chart.peak.bpm,
    })}
  >
    {chart.bands.map((band) => (
      <rect
        key={band.zoneIndex}
        x={chart.left}
        y={band.y}
        width={chart.width - chart.left}
        height={band.height}
        fill={zoneColors[band.zoneIndex]}
        fillOpacity={0.12}
      />
    ))}
    {chart.yTicks.map(({ bpm, y }) => (
      <React.Fragment key={bpm}>
        <line x1={chart.left} y1={y} x2={chart.width} y2={y} stroke={colors.shades.gamma8} strokeDasharray="2 4" />
        <text x={0} y={y + 4} {...AXIS_FONT}>{bpm}</text>
      </React.Fragment>
    ))}
    {chart.averageY !== null && (
      <>
        <line
          x1={chart.left}
          y1={chart.averageY}
          x2={chart.width}
          y2={chart.averageY}
          stroke={colors.base.beta}
          strokeOpacity={0.45}
          strokeDasharray="6 4"
        />
        <text x={chart.width - 4} y={chart.averageY - 5} textAnchor="end" {...AXIS_FONT}>
          {format(texts.average, { bpm: Math.round(averageHeartRate) })}
        </text>
      </>
    )}
    <polyline
      points={chart.points}
      fill="none"
      stroke={colors.base.danger}
      strokeWidth={2.5}
      strokeLinejoin="round"
      strokeLinecap="round"
    />
    <circle
      cx={chart.peak.x}
      cy={chart.peak.y}
      r={4.5}
      fill={colors.base.alpha}
      stroke={colors.base.danger}
      strokeWidth={2.5}
    />
    {chart.xTicks.map(({ x, minute }, index) => {
      const isFirst = index === 0;
      const isLast = index === chart.xTicks.length - 1;
      let anchor = 'middle';
      if (isFirst) anchor = 'start';
      if (isLast) anchor = 'end';
      return (
        <text key={x} x={x} y={chart.height - 4} textAnchor={anchor} {...AXIS_FONT}>
          {isLast ? `${minute} ${texts.min}` : minute}
        </text>
      );
    })}
  </ChartSvg>
);

HeartRateChart.propTypes = {
  chart: PropTypes.object.isRequired,
  averageHeartRate: PropTypes.number,
};

HeartRateChart.defaultProps = {
  averageHeartRate: null,
};

/**
 * One workout's heart rate over time, its time in each zone and its calories.
 */
const WorkoutHealthDetails = ({ activity, maxHeartRate }) => {
  const points = useMemo(() => activity.heartRateDataPoints, [activity]);

  const chart = useMemo(() => (points.length > 1 ? buildHeartRateChart({
    points,
    startDate: activity.startDate,
    endDate: activity.endDate,
    averageHeartRate: activity.averageHeartRate,
    maxHeartRate,
  }) : null), [activity, points, maxHeartRate]);

  const zoneSeconds = useMemo(() => getTimeInZones(points, maxHeartRate), [points, maxHeartRate]);
  const maxZoneSeconds = Math.max(...zoneSeconds, 1);

  const timeRange = activity.startDate && activity.endDate
    ? `${moment(activity.startDate).format('h:mm')} – ${moment(activity.endDate).format('h:mm A')}`
    : '';

  return (
    <DetailsRow>
      <DetailsCard $grow={2} $basis="420px">
        <CardHeader>
          <Strong>{format(texts.heartRate, { time: timeRange })}</Strong>
          <Muted>{texts.bpm}</Muted>
        </CardHeader>
        {chart
          ? <HeartRateChart chart={chart} averageHeartRate={activity.averageHeartRate} />
          : <Muted>{texts.noHeartRate}</Muted>}
      </DetailsCard>
      <DetailsCard>
        <CardHeader>
          <Strong>{texts.zones}</Strong>
        </CardHeader>
        {[...HeartRateZones].reverse().map((zone) => {
          const index = HeartRateZones.indexOf(zone);
          return (
            <ZoneRow key={zone.id}>
              <Strong>{zone.id}</Strong>
              <ZoneTrack>
                <ZoneFill $share={(zoneSeconds[index] / maxZoneSeconds) * 100} $color={zoneColors[index]} />
              </ZoneTrack>
              <ZoneTime>{formatDuration(zoneSeconds[index], ACTIVE_TIME_FORMAT)}</ZoneTime>
            </ZoneRow>
          );
        })}
        <CaloriesRow>
          <Muted>{texts.calories}</Muted>
          <span>
            <Strong>{formatNumber(activity.activeCalories)}</Strong>
            {` ${texts.active} · `}
            <Strong>{formatNumber(activity.basalEnergyBurned)}</Strong>
            {` ${texts.resting}`}
          </span>
        </CaloriesRow>
      </DetailsCard>
    </DetailsRow>
  );
};

WorkoutHealthDetails.propTypes = {
  activity: PropTypes.shape({
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    activeCalories: PropTypes.number,
    basalEnergyBurned: PropTypes.number,
    averageHeartRate: PropTypes.number,
    heartRateDataPoints: PropTypes.arrayOf(PropTypes.shape({
      date: PropTypes.instanceOf(Date),
      minHeartRate: PropTypes.number,
      maxHeartRate: PropTypes.number,
    })),
  }).isRequired,
  maxHeartRate: PropTypes.number.isRequired,
};

export default WorkoutHealthDetails;
